import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';

import { useUnreadNotificationCount } from '../../features/notifications/useNotifications';
import { useReducedMotion } from '../../shared/hooks/useMediaQuery';
import { PullToRefresh } from '../../shared/ui/PullToRefresh';
import { InstallBanner } from '../pwa/InstallBanner';
import { OfflineBanner } from '../pwa/OfflineBanner';

import s from './AppShell.module.css';
import { BottomTabBar } from './BottomTabBar';
import type { NavGroup, NavTabs } from './nav-types';
import { Sidebar } from './Sidebar';
import { useDrawerSwipe } from './useDrawerSwipe';
import { useSidebar } from './useSidebar';

const MAIN_ID = 'main-content';

interface AppShellProps {
  brand: string;
  workspace: string;
  homeHref: string;
  /** Where this workspace keeps the account screen. */
  profileHref: string;
  groups: readonly NavGroup[];
  /** The four destinations on the phone's bottom tab bar. */
  tabs: NavTabs;
}

export function AppShell({
  brand,
  workspace,
  homeHref,
  profileHref,
  groups,
  tabs,
}: AppShellProps): JSX.Element {
  const { collapsed, toggleCollapsed, isMobile, drawerOpen, openDrawer, closeDrawer } =
    useSidebar();
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  // Whichever control opened the drawer gets focus back when it closes.
  const lastOpenerRef = useRef<HTMLButtonElement | null>(null);

  // showModal() gives the focus trap, the ESC key and the top layer for free —
  // the same trade the shared Drawer primitive makes. Nothing here re-implements
  // any of the three.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (drawerOpen && !el.open) el.showModal();
    else if (!drawerOpen && el.open) el.close();
  }, [drawerOpen]);

  const { edgeProps, panelProps, requestClose } = useDrawerSwipe({
    enabled: isMobile,
    open: drawerOpen,
    onOpen: openDrawer,
    onClose: closeDrawer,
    dialogRef,
    panelRef,
    reducedMotion,
  });

  const openFromMenu = useCallback((): void => {
    lastOpenerRef.current = menuButtonRef.current;
    openDrawer();
  }, [openDrawer]);

  const openFromMore = useCallback((): void => {
    lastOpenerRef.current = moreButtonRef.current;
    openDrawer();
  }, [openDrawer]);

  // Fires for ESC as well as for our own close(), so it is the one place that
  // has to hand focus back to the control the drawer was opened from.
  const handleDialogClose = useCallback((): void => {
    closeDrawer();
    (lastOpenerRef.current ?? menuButtonRef.current)?.focus();
  }, [closeDrawer]);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>): void => {
      if (event.target === dialogRef.current) requestClose();
    },
    [requestClose],
  );

  // No filter on purpose: TanStack marks everything stale but only refetches
  // queries with a mounted observer — exactly the screen on display — and a
  // per-route key registry would go stale the day someone adds a query.
  const refresh = useCallback((): Promise<void> => queryClient.invalidateQueries(), [queryClient]);

  // Asked of the nav rather than passed in beside it: a workspace that draws no
  // notifications row (admin) is exactly a workspace that should not be paying
  // for the count query and its realtime channel, and deriving it means the two
  // cannot fall out of step.
  const wantsUnreadCount = groups.some((group) =>
    group.items.some((item) => item.badge === 'notifications'),
  );
  const unread = useUnreadNotificationCount(wantsUnreadCount);
  const badges = useMemo(() => ({ notifications: unread }), [unread]);

  return (
    <div className={collapsed ? `${s.root} ${s.rail}` : s.root}>
      <a className={s.skip} href={`#${MAIN_ID}`}>
        Skip to content
      </a>

      {isMobile ? (
        <header className={s.mobileBar}>
          <button
            ref={menuButtonRef}
            type="button"
            className={s.menuButton}
            onClick={openFromMenu}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M3.25 5.5h13.5M3.25 10h13.5M3.25 14.5h13.5" />
            </svg>
          </button>
          <span className={s.mobileBrand}>
            <span className={s.mobileMark} aria-hidden="true">
              i
            </span>
            <span className={s.mobileTitle}>{workspace}</span>
          </span>
        </header>
      ) : (
        <aside className={s.aside}>
          <Sidebar
            brand={brand}
            workspace={workspace}
            homeHref={homeHref}
            profileHref={profileHref}
            groups={groups}
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
            badges={badges}
          />
        </aside>
      )}

      {/* Mounted only in the mobile branch so the full nav never exists twice
          in the accessibility tree. The tab bar below is a different landmark
          with a SUBSET of destinations; tests scope by landmark name. */}
      {isMobile ? (
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,
                                     jsx-a11y/click-events-have-key-events --
           The <dialog> box is the backdrop here; a click landing on it (rather
           than on the panel inside) dismisses. ESC is already handled natively
           through onClose, so there is no keyboard path left uncovered — the
           same trade shared/ui/Drawer makes for its backdrop. */
        <dialog
          ref={dialogRef}
          className={s.drawer}
          onClose={handleDialogClose}
          onClick={handleBackdropClick}
          aria-label={`${brand} navigation`}
        >
          <div ref={panelRef} className={s.drawerPanel} {...panelProps}>
            <Sidebar
              brand={brand}
              workspace={workspace}
              homeHref={homeHref}
              profileHref={profileHref}
              groups={groups}
              collapsed={false}
              badges={badges}
              viewTransition={false}
              closeControl={
                <button
                  type="button"
                  className={s.drawerClose}
                  onClick={requestClose}
                  aria-label="Close navigation menu"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="m4 4 8 8M12 4l-8 8" />
                  </svg>
                </button>
              }
            />
          </div>
        </dialog>
      ) : null}

      {/* The invisible strip along the left edge that starts a swipe-open. Only
          while the drawer is closed, so it never sits over an open panel. */}
      {isMobile && !drawerOpen ? (
        <div className={s.edgeZone} aria-hidden="true" {...edgeProps} />
      ) : null}

      <div className={s.column}>
        <OfflineBanner />
        <InstallBanner />
        <main className={s.main} id={MAIN_ID} tabIndex={-1}>
          <div className={s.mainInner}>
            <PullToRefresh enabled={isMobile} onRefresh={refresh}>
              <Outlet />
            </PullToRefresh>
          </div>
        </main>
      </div>

      {isMobile ? (
        <BottomTabBar
          tabs={tabs}
          moreOpen={drawerOpen}
          onMore={openFromMore}
          moreButtonRef={moreButtonRef}
        />
      ) : null}
    </div>
  );
}
